<!-- TEST-KOMMENTAR: Änderung durch KI eingefügt am Anfang der Datei -->
<template>
  <div class="drive-start-view">
    <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
      <div class="flex justify-between items-center mb-6 bg-white rounded-xl p-5">
        <div>
          <h1 class="text-xl font-bold text-gray-800 mb-1">Neue Fahrt starten</h1>
          <p v-if="!showLicensePlateInput" class="text-sm text-gray-600">Scanne das Kennzeichen oder gib es manuell ein</p>
          <p v-else-if="showLicensePlateInput && !searchCompleted" class="text-sm text-gray-600">Bitte gib ein Kennzeichen ein</p>
          <p v-else-if="showLicensePlateInput && searchCompleted && !vehicleFound" class="text-sm text-gray-600">Fahrzeug nicht gefunden. Bitte vervollständige die Daten manuell.</p>
          <p v-else-if="showLicensePlateInput && searchCompleted && vehicleFound && !canStartDrive" class="text-sm text-gray-600">Fahrzeug gefunden, aber noch nicht bereit für Fahrtstart.</p>
          <p v-else-if="showLicensePlateInput && searchCompleted && vehicleFound && canStartDrive" class="text-sm text-gray-600">Fahrzeug gefunden und bereit für Fahrtstart.</p>
        </div>
        <div>
          <svg xmlns="http://www.w3.org/2000/svg" class="h-10 w-10 text-[#E9041D] opacity-90" fill="none" viewBox="0 0 24 24" stroke="currentColor">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
        </div>
      </div>

      <div class="bg-white rounded-lg shadow-sm overflow-hidden">
        <div class="p-4">
          <form @submit.prevent="startDrive">
            <!-- Kennzeichen-Scan oder Eingabe -->
            <div class="space-y-4">
              <div>
                <!-- Scan-Button und manuelle Eingabe-Option -->
                <div v-if="!showLicensePlateInput" class="space-y-4">
                  <!-- Entfernt: Terminal-Auswahl vor Fahrzeugsuche -->
                  <button 
                    type="button"
                    @click="startScanning"
                    :disabled="false"
                    class="w-full py-4 px-4 bg-[#E9041D] text-white rounded-lg hover:bg-red-700 transition-colors text-base font-medium flex items-center justify-center disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 9a2 2 0 012-2h.93a2 2 0 001.664-.89l.812-1.22A2 2 0 0110.07 4h3.86a2 2 0 011.664.89l.812 1.22A2 2 0 0018.07 7H19a2 2 0 012 2v9a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 13a3 3 0 11-6 0 3 3 0 016 0z" />
                    </svg>
                    Kennzeichen scannen
                  </button>
                  
                  <div class="text-center">
                    <button 
                      type="button"
                      @click="handleManualEntry"
                      :disabled="false"
                      class="text-sm text-[#E9041D] hover:text-red-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      Kennzeichen manuell erfassen
                    </button>
                  </div>
                </div>
                <!-- Nach Fahrzeugsuche: Start- & Zielort-Auswahl anzeigen -->
                <div v-if="showLicensePlateInput">
                  <!-- Kennzeichen-Input mit Länderauswahl -->
                  <LicensePlateInput
                    ref="licensePlateInputRef"
                    v-model="licensePlate"
                    :disabled="isOnline && searchCompleted"
                    required
                  />
                  <!-- Start- & Zielort-Auswahl erst nach erfolgreicher Suche anzeigen -->
                  <div v-if="searchCompleted && (vehicleFound || !isOnline)">
                    <div class="mb-4">
                      <label for="terminal" class="block text-sm font-medium text-gray-700 mb-1">Startort</label>
                      <select 
                        id="terminal"
                        v-model="startLocation"
                        class="w-full rounded-lg border border-gray-300 shadow-sm focus:border-[#E9041D] focus:outline-none h-12 text-base px-4 bg-white"
                        required
                        :disabled="!manualLocationEdit"
                      >
                        <option value="" disabled>Bitte wählen</option>
                        <option v-for="location in availableLocations" :key="location.id" :value="location.id" class="py-2">
                          {{ location.name }}
                        </option>
                      </select>
                    </div>
                    <div class="mb-4">
                      <label for="destination" class="block text-sm font-medium text-gray-700 mb-1">Zielort</label>
                      <select 
                        id="destination"
                        v-model="destinationLocation"
                        class="w-full rounded-lg border border-gray-300 shadow-sm focus:border-[#E9041D] focus:outline-none h-12 text-base px-4 bg-white"
                        required
                        :disabled="!manualLocationEdit"
                      >
                        <option value="" disabled>Bitte wählen</option>
                        <option v-for="location in availableLocations" :key="location.id" :value="location.id" class="py-2">
                          {{ location.name }}
                        </option>
                      </select>
                    </div>
                    <div v-if="!manualLocationEdit" class="text-right">
                      <button type="button" @click="manualLocationEdit = true" class="text-sm text-[#E9041D] hover:text-red-700 font-medium">Start- & Zielort manuell eingeben</button>
                    </div>
                  </div>
                  <div v-if="manualLocationEdit && startLocation === destinationLocation && availableLocations.length > 0" class="mb-4 p-3 bg-yellow-50 border border-yellow-200 rounded text-yellow-800 text-sm">
                    Keine ausstehende Fahrt gefunden. Bitte Start- & Zielort manuell wählen.
                  </div>
                </div>
              </div>

              <!-- Lade-Animation während der Suche -->
              <div v-if="showLicensePlateInput && isValidLicensePlate && isSearching" class="py-6 flex flex-col items-center justify-center">
                <div class="animate-spin rounded-full h-10 w-10 border-b-2 border-[#E9041D] mb-3"></div>
                <p class="text-gray-600">Suche nach Fahrzeug...</p>
              </div>

              <!-- Check-In-Status Anzeige -->
              <div v-if="searchCompleted && vehicleFound && checkInStatus" class="space-y-3">
                <div class="p-4 rounded-lg border" :class="{
                  'bg-green-50 border-green-200': canStartDrive,
                  'bg-red-50 border-red-200': !canStartDrive
                }">
                  <div class="flex items-center">
                    <svg v-if="canStartDrive" xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-green-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
                    </svg>
                    <svg v-else xmlns="http://www.w3.org/2000/svg" class="h-5 w-5 text-red-500 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                      <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                    </svg>
                    <div>
                      <h3 class="text-sm font-medium" :class="{
                        'text-green-800': canStartDrive,
                        'text-red-800': !canStartDrive
                      }">
                        Check-In-Status
                      </h3>
                      <p class="text-sm" :class="{
                        'text-green-700': canStartDrive,
                        'text-red-700': !canStartDrive
                      }">
                        <span v-if="checkInStatus === 'completed'">Check-In ist abgeschlossen</span>
                        <span v-else-if="checkInStatus === 'closed'">Check-In ist geschlossen - Fahrt möglich</span>
                        <span v-else-if="checkInStatus === 'open_ready'">Services abgeschlossen - Fahrt möglich</span>
                        <span v-else-if="checkInStatus === 'open'">Check-In ist noch offen - Services werden bearbeitet</span>
                        <span v-else-if="checkInStatus === 'no_checkin'">Kein Check-In vorhanden - Fahrt nicht möglich</span>
                        <span v-else>Check-In Status: {{ checkInStatus }}</span>
                      </p>
                    </div>
                  </div>
                </div>

                <!-- Fehlermeldung -->
                <div v-if="checkInError" class="p-4 bg-red-50 border border-red-200 rounded-lg">
                  <p class="text-sm text-red-700">{{ checkInError }}</p>
                </div>
              </div>
            </div>

            <!-- Navigation Buttons -->
            <div class="mt-6 flex flex-col space-y-3">
              <button 
                v-if="showLicensePlateInput && !searchCompleted"
                type="submit"
                :disabled="!isValidLicensePlate || isSearching"
                class="w-full px-4 py-4 bg-[#E9041D] text-white rounded-lg hover:bg-red-700 transition-colors text-base font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span v-if="isSearching">Suche läuft...</span>
                <span v-else>Fahrzeug suchen</span>
              </button>
              <button 
                v-if="searchCompleted"
                type="button"
                @click="createDrive"
                :disabled="!canStartDrive"
                class="w-full px-4 py-4 bg-[#E9041D] text-white rounded-lg hover:bg-red-700 transition-colors text-base font-medium disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <span v-if="!canStartDrive && isOnline">Fahrt kann nicht gestartet werden</span>
                <span v-else-if="!canStartDrive && !isOnline">Bitte alle Felder ausfüllen</span>
                <span v-else-if="!isOnline">Fahrt offline starten</span>
                <span v-else>Fahrt starten</span>
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>

    <!-- License Plate Scanner Modal -->
    <LicensePlateScanner 
      :is-open="showScanner"
      user-role="driver"
      @close="showScanner = false"
      @scanned="handleScanned"
      @manual="handleManualEntry"
    />
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted, onUnmounted, computed, watch, nextTick } from 'vue'
import { useRouter } from 'vue-router'
import api from '../api/axios'
import { useAuth } from '../composables/useAuth'
import { saveOfflineAction, saveLocationsToCache, getLocationsFromCache } from '../services/offlineSync'
import { v4 as uuidv4 } from 'uuid'
import LicensePlateInput from '../components/LicensePlateInput.vue'
import LicensePlateScanner from '../components/LicensePlateScanner.vue'

interface Location {
  id: number
  name: string
}

interface User {
  id: number
  name: string
  email: string
  email_verified_at: string | null
  created_at: string
  updated_at: string
  role_name: string
  airport_id?: number
}

interface CurrentUser {
  id: number | null
  airport_id: number | null
}

const router = useRouter()
const showLicensePlateInput = ref(false)
const showScanner = ref(false)
const isScanned = ref(false)

const { state: authState, fetchUser } = useAuth()
const isLoadingUser = ref(false)
const currentUser = ref<CurrentUser>({
  id: null,
  airport_id: null
})

const licensePlate = ref('')
const startLocation = ref('')
const destinationLocation = ref('')
const manualLocationEdit = ref(false)

// Flag für die Suche nach Fahrzeugen
const isSearching = ref(false)
const vehicleFound = ref(false)
const searchCompleted = ref(false)

// Reaktive Variablen für Check-In-Status
const checkInStatus = ref<string | null>(null)
const checkInError = ref<string | null>(null)
const canStartDrive = ref(false)

// Standortliste
const availableLocations = ref<Location[]>([])
const isLoadingLocations = ref(false)

// Referenz zur LicensePlateInput-Komponente
const licensePlateInputRef = ref()

// Computed property für die Validierung des Kennzeichens
const isValidLicensePlate = computed(() => {
  if (!licensePlateInputRef.value) return false
  return licensePlateInputRef.value.isValidFormat
})

const isOnline = computed(() => navigator.onLine)

onMounted(async () => {
  try {
    console.log('Component mounted, initializing...')
    if (!isOnline.value) {
      // OFFLINE: User und Standorte aus LocalStorage/Cache
      const userStr = localStorage.getItem('user')
      if (userStr) {
        try {
          const user = JSON.parse(userStr)
          authState.value.user = user
          authState.value.isAuthenticated = true
          currentUser.value = { id: user.id, airport_id: user.airport_id }
          console.log('OFFLINE: User aus LocalStorage:', user)
          if (user.airport_id) {
            const cached = await getLocationsFromCache(user.airport_id)
            if (cached && Array.isArray(cached)) {
              availableLocations.value = cached
              console.log('OFFLINE: Standorte aus Cache:', cached)
            } else {
              availableLocations.value = []
              console.warn('OFFLINE: Keine Standorte im Cache gefunden')
            }
          }
        } catch (e) {
          console.warn('OFFLINE: User konnte nicht aus LocalStorage geparst werden')
        }
      } else {
        console.warn('OFFLINE: Kein User im LocalStorage gefunden')
      }
    } else {
      // ONLINE: User und Standorte aus API
      await fetchUserData()
      if (authState.value.user && authState.value.user.airport_id) {
        localStorage.setItem('user', JSON.stringify(authState.value.user))
        // Standorte werden in fetchUserData gecacht
      }
    }
    console.log('Initialisierung abgeschlossen. availableLocations:', availableLocations.value)
  } catch (error) {
    console.error('Error during initialization:', error)
  }
})

const fetchUserData = async () => {
  isLoadingUser.value = true
  try {
    if (!authState.value.user) {
      if (!isOnline.value) {
        // Offline: Versuche User aus LocalStorage zu laden
        const userStr = localStorage.getItem('user')
        if (userStr) {
          try {
            const user = JSON.parse(userStr)
            authState.value.user = user
            authState.value.isAuthenticated = true
            console.log('Offline: User aus LocalStorage geladen:', user)
          } catch (e) {
            console.warn('Offline: User konnte nicht aus LocalStorage geparst werden')
          }
        }
      } else {
        console.log('Benutzerdaten nicht vorhanden, lade sie...')
        await fetchUser()
        // Nach erfolgreichem Login User im LocalStorage speichern
        if (authState.value.user) {
          localStorage.setItem('user', JSON.stringify(authState.value.user))
        }
      }
    }
    console.log('Verwende vorhandene Benutzerdaten:', authState.value.user)
    const user = authState.value.user as User
    console.log('User object:', user)
    console.log('User airport_id:', user?.airport_id)
    if (user?.id && user?.airport_id) {
      currentUser.value = {
        id: user.id,
        airport_id: user.airport_id
      }
      console.log('Aktueller Benutzer:', currentUser.value)
      // Lade Standorte für den Flughafen des Benutzers
      await fetchAirportLocations(user.airport_id)
    } else {
      console.error('Benutzerdaten sind unvollständig oder haben nicht das erwartete Format')
      console.error('Erhaltene Daten:', authState.value.user)
    }
  } catch (error) {
    console.error('Fehler beim Verarbeiten der Benutzerdaten:', error)
  } finally {
    isLoadingUser.value = false
  }
}

// Lade Standorte für einen bestimmten Flughafen
const fetchAirportLocations = async (airportId: number) => {
  isLoadingLocations.value = true
  try {
    if (!isOnline.value) {
      // Offline: Lade aus Cache
      const cached = await getLocationsFromCache(airportId)
      if (cached && Array.isArray(cached)) {
        availableLocations.value = cached
        console.log('Standorte aus Offline-Cache geladen:', cached)
        console.log('DEBUG availableLocations:', availableLocations.value)
      } else {
        availableLocations.value = []
        console.warn('Keine Standorte im Offline-Cache gefunden')
      }
      return
    }
    // Online: Lade von API
    console.log('Lade Flughafenstandorte für Airport ID:', airportId)
    const response = await api.get(`/api/airports/${airportId}`)
    if (response.data.data && response.data.data.locations && Array.isArray(response.data.data.locations)) {
      availableLocations.value = response.data.data.locations
      // Nach erfolgreichem Laden in den Cache speichern
      await saveLocationsToCache(airportId, availableLocations.value)
      console.log('Standorte von API geladen und im Cache gespeichert:', availableLocations.value)
    } else {
      availableLocations.value = []
      console.error('Keine Standorte in der API-Antwort gefunden')
    }
  } catch (error: any) {
    console.error('Fehler beim Abrufen der Flughafenstandorte:', error)
    if (error.response) {
      console.error('API Fehlerdetails:', error.response.data)
    }
  } finally {
    isLoadingLocations.value = false
  }
}

// Füge einen Watch für availableLocations hinzu
watch(availableLocations, (newLocations) => {
  console.log('availableLocations wurde aktualisiert:', newLocations)
}, { deep: true })

// Füge einen Watch für startLocation hinzu
watch(startLocation, (newLocation) => {
  console.log('startLocation wurde aktualisiert:', newLocation)
})

// Nach Fahrzeugsuche: Setze Start- und Zielort automatisch, falls vorhanden
watch([searchCompleted, vehicleFound], async ([completed, found]) => {
  if (completed && found && !manualLocationEdit.value) {
    // Hole die Fahrzeugdaten aus dem letzten Such-Response
    try {
      const response = await api.post('/api/vehicles/search', {
        license_plate: licensePlate.value
      })
      const vehicle = response.data.data && response.data.data.length > 0 ? response.data.data[0] : null
      if (vehicle) {
        // Priority-based location selection
        let start = null
        let end = null
        
        // 1. First priority: Use suggested_route if available
        if (vehicle.suggested_route) {
          start = vehicle.suggested_route.start_location
          end = vehicle.suggested_route.end_location
          console.log('Using suggested route:', vehicle.suggested_route.reason)
        }
        // 2. Second priority: Use last_checkin for fresh check-ins
        else if (vehicle.has_untransported_checkin && vehicle.last_checkin) {
          start = vehicle.last_checkin.start_location
          end = vehicle.last_checkin.end_location
          console.log('Using untransported checkin locations')
        }
        // 3. Third priority: Existing mismatch logic (backward compatibility)
        else if (vehicle.checkin_locations_mismatch === true && vehicle.last_checkin) {
          start = vehicle.last_checkin.start_location
          end = vehicle.last_checkin.end_location
          console.log('Using checkin locations due to mismatch')
        }
        // 4. Fourth priority: Use last transport if no mismatch
        else if (vehicle.checkin_locations_mismatch === false && vehicle.last_transport) {
          start = vehicle.last_transport.start_location
          end = vehicle.last_transport.end_location
          console.log('Using last transport locations')
        }
        // 5. Fifth priority: If only transport exists, car is at its last destination
        else if (vehicle.last_transport && vehicle.last_transport.end_location) {
          start = vehicle.last_transport.end_location
          end = null // No predetermined destination
          console.log('Vehicle at last transport destination, no predetermined route')
        }
        
        // Set the location values based on what we found
        if (start && end) {
          const startId = String(start.id)
          const endId = String(end.id)
          const startExists = availableLocations.value.some(loc => String(loc.id) === startId)
          const endExists = availableLocations.value.some(loc => String(loc.id) === endId)
          startLocation.value = startExists ? startId : ''
          destinationLocation.value = endExists ? endId : ''
          // Enable manual edit if start and end are the same
          if (startId === endId) {
            manualLocationEdit.value = true
          }
        } else if (start && !end) {
          // Only start location known
          const startId = String(start.id)
          const startExists = availableLocations.value.some(loc => String(loc.id) === startId)
          startLocation.value = startExists ? startId : ''
          destinationLocation.value = ''
          manualLocationEdit.value = true
        } else if (availableLocations.value.length > 0) {
          // Fallback to first available location
          startLocation.value = String(availableLocations.value[0].id)
          destinationLocation.value = String(availableLocations.value[0].id)
          manualLocationEdit.value = true
          console.log('No location data available, using defaults')
        } else {
          startLocation.value = ''
          destinationLocation.value = ''
          manualLocationEdit.value = true
        }
      }
    } catch (e) {
      // Fallback: Standardverhalten
      if (availableLocations.value.length > 0) {
        startLocation.value = String(availableLocations.value[0].id)
        destinationLocation.value = String(availableLocations.value[0].id)
      } else {
        startLocation.value = ''
        destinationLocation.value = ''
      }
    }
  }
})

// Nach den ref-Definitionen:
watch([
  licensePlate, startLocation, destinationLocation, searchCompleted, manualLocationEdit],
  ([plate, start, dest, completed, manualEdit]) => {
    if (!isOnline.value && completed && manualEdit) {
      canStartDrive.value = !!(plate && start && dest)
    }
  }
)

const startScanning = () => {
  showScanner.value = true
}

// Handler functions for the scanner
const handleScanned = (scannedLicensePlate: string) => {
  licensePlate.value = scannedLicensePlate
  showLicensePlateInput.value = true
  isScanned.value = true
  console.log('License plate detected:', scannedLicensePlate)
}

const handleManualEntry = () => {
  showLicensePlateInput.value = true
  isScanned.value = false
  // Focus the license plate input after a small delay
  nextTick(() => {
    if (licensePlateInputRef.value) {
      licensePlateInputRef.value.focus()
    }
  })
}

// Old captureImage function removed - functionality now handled by LicensePlateScanner component

onUnmounted(() => {
  // Cleanup handled by LicensePlateScanner component
})

const startDrive = async () => {
  if (!licensePlate.value || !isValidLicensePlate.value) {
    return
  }

  isSearching.value = true
  searchCompleted.value = false
  checkInStatus.value = null
  checkInError.value = null
  canStartDrive.value = false

  try {
    if (!isOnline.value) {
      // Prüfe, ob Standorte im Cache sind (Dummy für Fahrzeug-Existenz)
      let vehicleExistsOffline = false
      if (availableLocations.value.length === 0) {
        // Versuche Standorte aus Cache nachzuladen
        const userStr = localStorage.getItem('user')
        if (userStr) {
          try {
            const user = JSON.parse(userStr)
            if (user.airport_id) {
              const cached = await getLocationsFromCache(user.airport_id)
              if (cached && Array.isArray(cached) && cached.length > 0) {
                availableLocations.value = cached
                vehicleExistsOffline = true
              }
            }
          } catch (e) {}
        }
      } else {
        vehicleExistsOffline = true
      }
      if (!vehicleExistsOffline) {
        alert('Fahrzeug oder Standorte nicht im Offline-Cache gefunden. Bitte online synchronisieren.')
        isSearching.value = false
        searchCompleted.value = false
        return
      }
      // Fahrzeug "gefunden" (Dummy, da wir offline keine Details prüfen können)
      vehicleFound.value = true
      searchCompleted.value = true
      manualLocationEdit.value = true
      // Default-Auswahl setzen
      if (availableLocations.value.length > 0) {
        startLocation.value = String(availableLocations.value[0].id)
        destinationLocation.value = String(availableLocations.value[0].id)
      }
      canStartDrive.value = !!(licensePlate.value && startLocation.value && destinationLocation.value)
      if (!canStartDrive.value) {
        alert('Bitte fülle alle Felder aus, um die Fahrt offline zu speichern.')
        isSearching.value = false
        searchCompleted.value = true
        return
      }
      const offlineId = uuidv4();
      await saveOfflineAction({
        id: offlineId,
        type: 'startDrive',
        payload: {
          license_plate: licensePlate.value,
          start_location_id: startLocation.value,
          end_location_id: destinationLocation.value
        },
        createdAt: Date.now()
      })
      isSearching.value = false
      searchCompleted.value = true
      // Nach dem Speichern zur Offline-Fahrt navigieren
      router.replace({ name: 'drive-active', params: { id: offlineId } })
      return
    }
    console.log('Sende API-Anfrage mit Kennzeichen:', licensePlate.value)
    
    const response = await api.post('/api/vehicles/search', {
      license_plate: licensePlate.value
    })
    
    console.log('API-Antwort erhalten:', response.data)
    
    if (response.data.data && response.data.data.length > 0) {
      vehicleFound.value = true
      
      // Prüfe den Check-In-Status des Fahrzeugs
      const vehicle = response.data.data[0]
      console.log('Fahrzeug gefunden:', vehicle)
      
      // Hole die Fahrzeugdetails mit Check-Ins
      try {
        const vehicleDetailsResponse = await api.get(`/api/vehicles/${vehicle.id}`)
        console.log('Fahrzeugdetails:', vehicleDetailsResponse.data)
        
        const vehicleDetails = vehicleDetailsResponse.data
        const checkIns = vehicleDetails.check_ins || {}
        console.log('Check-Ins aus Fahrzeugdetails:', checkIns)
        
        // Konvertiere das Objekt zu einem Array für einfachere Verarbeitung
        const checkInsArray = Object.values(checkIns) as any[]
        console.log('Check-Ins als Array:', checkInsArray)
        
        if (checkInsArray.length > 0) {
          // Der neueste Check-In ist bereits am Anfang des Arrays
          const latestCheckIn = checkInsArray[0] as any
          
          console.log('Letzter Check-In:', latestCheckIn)
          console.log('Check-In Status:', latestCheckIn.status)
          console.log('Vehicle Key:', latestCheckIn.vehicle_key)
          console.log('Status-Typ:', typeof latestCheckIn.status)
          checkInStatus.value = latestCheckIn.status
          
          // Get key status if available
          const keyStatus = latestCheckIn.vehicle_key?.status || null
          console.log('Key Status:', keyStatus)
          
          // Allow drive if:
          // 1. Check-in is closed OR
          // 2. Check-in is open BUT key is ready (all services completed)
          if (latestCheckIn.status === 'closed' || 
              (latestCheckIn.status === 'open' && keyStatus === 'ready')) {
            canStartDrive.value = true
            console.log('Fahrt kann gestartet werden - Check-In:', latestCheckIn.status, 'Key:', keyStatus)
            console.log('canStartDrive gesetzt auf:', canStartDrive.value)
            
            // Update message based on scenario
            if (latestCheckIn.status === 'open' && keyStatus === 'ready') {
              checkInStatus.value = 'open_ready' // Special status for UI display
            }
          } else {
            canStartDrive.value = false
            
            // Provide specific error message based on status
            if (latestCheckIn.status === 'open' && keyStatus === 'in_service') {
              checkInError.value = `Das Fahrzeug wird noch bearbeitet. Services sind noch nicht abgeschlossen.`
            } else if (latestCheckIn.status === 'open' && !keyStatus) {
              checkInError.value = `Das Fahrzeug hat einen offenen Check-In ohne Schlüssel. Bitte wenden Sie sich an einen Mitarbeiter.`
            } else {
              checkInError.value = `Das Fahrzeug hat einen aktiven Check-In mit Status: ${latestCheckIn.status}. Die Fahrt kann erst gestartet werden, wenn der Check-In abgeschlossen ist.`
            }
            
            console.log('Fahrt kann nicht gestartet werden:', latestCheckIn.status, 'Key:', keyStatus)
            console.log('canStartDrive gesetzt auf:', canStartDrive.value)
          }
        } else {
          canStartDrive.value = false
          checkInStatus.value = 'no_checkin'
          checkInError.value = 'Das Fahrzeug hat keinen Check-In. Ein Check-In muss zuerst abgeschlossen werden.'
          console.log('Kein Check-In gefunden, Fahrt kann nicht gestartet werden')
        }
      } catch (detailsError: any) {
        console.error('Fehler beim Abrufen der Fahrzeugdetails:', detailsError)
        checkInError.value = 'Die Fahrzeugdetails konnten nicht abgerufen werden.'
        canStartDrive.value = false
      }
    } else {
      vehicleFound.value = false
      checkInStatus.value = null
      canStartDrive.value = false
    }
  } catch (error: any) {
    console.error('Fehler bei der Suche nach dem Fahrzeug:', error)
    console.error('Fehlerdetails:', error.response?.data)
    vehicleFound.value = false
    checkInError.value = 'Fahrzeug konnte nicht gefunden werden.'
    canStartDrive.value = false
  } finally {
    isSearching.value = false
    searchCompleted.value = true
  }
}

const createDrive = async () => {
  if (!licensePlate.value || !startLocation.value || !destinationLocation.value) {
    console.error('Nicht alle erforderlichen Felder sind ausgefüllt')
    return
  }
  
  try {
    // Suche nach der Fahrzeug-ID
    const searchResponse = await api.post('/api/vehicles/search', {
      license_plate: licensePlate.value
    })
    
    console.log('Fahrzeug-Suche Antwort:', searchResponse.data)
    
    let vehicleId: number | null = null
    
    if (searchResponse.data.data && searchResponse.data.data.length > 0) {
      vehicleId = searchResponse.data.data[0].id
    } else {
      console.error('Fahrzeug nicht gefunden')
      return
    }
    
    // Erstelle einen neuen Transport
    if (vehicleId && currentUser.value.id) {
      const transportResponse = await api.post('/api/driver/transports/start', {
        vehicle_id: vehicleId,
        start_location_id: startLocation.value,
        end_location_id: destinationLocation.value
      })
      
      console.log('Transport erstellt - Vollständige Antwort:', transportResponse)
      
      if (transportResponse.data && transportResponse.data.data && transportResponse.data.data.id) {
        const transportId = transportResponse.data.data.id
        console.log('Navigiere zu aktiver Fahrt mit ID:', transportId)
        
        // Direkte Navigation zur aktiven Fahrt
        router.replace({
          name: 'drive-active',
          params: { id: transportId.toString() }
        })
      } else {
        console.error('Ungültiges API-Response-Format:', transportResponse.data)
      }
    }
  } catch (error: any) {
    console.error('Fehler beim Erstellen des Transports:', error)
    if (error.response?.data) {
      console.error('API Fehlerdetails:', error.response.data)
    }
  }
}

// Old handleManualLicensePlateInput function removed - functionality now handled by handleManualEntry
</script> 